import { FILTER_SUBMENU_OPTION_DELIMITER } from 'modules/missionControl/constants';
import { useMemo } from 'react';
import styled from 'styled-components';
import { BUTTON_VARIANTS, IconButton } from 'ui/button';
import { FlexSpacer } from 'ui/flexSpacer';
import { Exit16 } from 'ui/icons/aurora/16px/Exit16';
import { Filter16 } from 'ui/icons/aurora/16px/Filter16';
import { ALIGNMENT, Menu } from 'ui/menu';
import { CountBadge, MenuItemTextWrapper, MenuListContainerWrapper } from 'ui/menu/Styled';
import { spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { LaunchpadFilter } from './Launchpad.constants';
import { useLaunchpadContext } from './contexts/LaunchpadContext';
import { useLaunchpadFilterOptions } from './hooks/useLaunchpadFilterOptions';

const StyledMenu = styled(Menu)`
  ${MenuListContainerWrapper} {
    min-width: ${pxToRem(200)};
  }
  ${MenuItemTextWrapper} {
    width: 100%;
    justify-content: space-between;
    padding-right: ${spacing.half};
  }
`;

export const LAUNCHPAD_FILTERS_TEST_ID = 'launchpad-filters';

export const Filters = () => {
  const {
    resourceTypes = [],
    actionTypes = [],
    setActionTypes,
    setResourceTypes,
    handleClearFilters,
  } = useLaunchpadContext();

  const selectedOptionIds = useMemo(() => {
    const selectedActionTypes = actionTypes.map(
      (actionType) =>
        `${LaunchpadFilter.ACTION_TYPE}${FILTER_SUBMENU_OPTION_DELIMITER}${actionType}`,
    );
    const selectedResourceTypes = resourceTypes.map(
      (resourceType) =>
        `${LaunchpadFilter.RESOURCE_TYPE}${FILTER_SUBMENU_OPTION_DELIMITER}${resourceType}`,
    );
    return [...selectedActionTypes, ...selectedResourceTypes];
  }, [actionTypes, resourceTypes]);

  const handleFilterOptionChange = (id: string) => {
    const [filterType, filterId] = id.split(FILTER_SUBMENU_OPTION_DELIMITER);

    switch (filterType) {
      case LaunchpadFilter.RESOURCE_TYPE:
        if (resourceTypes.includes(filterId)) {
          setResourceTypes(resourceTypes.filter((resourceType) => resourceType !== filterId));
        } else {
          setResourceTypes([...resourceTypes, filterId]);
        }
        break;
      case LaunchpadFilter.ACTION_TYPE:
        if (actionTypes.includes(filterId)) {
          setActionTypes(actionTypes.filter((actionType) => actionType !== filterId));
        } else {
          setActionTypes([...actionTypes, filterId]);
        }
        break;
      default:
        // biome-ignore lint/suspicious/noConsole: debug
        console.warn('Unknown filter type:', filterType);
    }
  };

  const selectedFiltersCount = selectedOptionIds.length;
  const hasFiltersSelected = selectedFiltersCount > 0;
  const filterOptions = useLaunchpadFilterOptions({
    [LaunchpadFilter.ACTION_TYPE]: !!actionTypes.length,
    [LaunchpadFilter.RESOURCE_TYPE]: !!resourceTypes.length,
  });

  return (
    <FlexSpacer alignItems="center">
      <StyledMenu
        options={filterOptions}
        defaultSelectedOptionIds={selectedOptionIds}
        verticalAlignment={ALIGNMENT.DOWN}
        horizontalAlignment={ALIGNMENT.LEFT}
        handleSelectionChange={handleFilterOptionChange}
        data-test={LAUNCHPAD_FILTERS_TEST_ID}
      >
        <IconButton variant={BUTTON_VARIANTS.GHOST}>
          <Filter16 />
        </IconButton>
      </StyledMenu>
      {hasFiltersSelected && (
        <>
          <CountBadge data-test="filters-trigger-button-count-badge">
            {selectedFiltersCount}
          </CountBadge>

          <IconButton variant={BUTTON_VARIANTS.GHOST} onClick={handleClearFilters}>
            <Exit16 />
          </IconButton>
        </>
      )}
    </FlexSpacer>
  );
};
